import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { LuCalendarDays, LuCircleCheck, LuCircleX, LuClock, LuFileText, LuPencil, LuPlus, LuReceipt, LuTag } from 'react-icons/lu';
import Badge from '../../../components/ui/Badge';
import Button from '../../../components/ui/Button';
import SegmentedControl from '../../../components/ui/SegmentedControl';
import { useTranslation } from '../../../i18n/LanguageContext';
import { CARD, EmptyState, LoadingRows, SectionHeader } from '../components/ProfileUi';
import EditExpenseDialog from './EditExpenseDialog';
import { EXPENSE_STATUS_TONES, attachedPdfs, expenseCategoryLabel } from './expenseOptions';

const STATUS_ICONS = { pending: LuClock, approved: LuCircleCheck, rejected: LuCircleX };
const STATUSES = ['pending', 'approved', 'rejected'];

/**
 * "Masraf Takip": the user's expense requests with their status. Pending
 * ones can still be edited; a rejected one shows why.
 */
const ExpenseTracking = ({ requests, setRequests, loading, employeeId, onNewRequest }) => {
  const { t, locale } = useTranslation();
  const label = (key, vars) => t(`profilePage.expense.tracking.${key}`, vars);
  const [filter, setFilter] = useState('all');
  const [editingExpense, setEditingExpense] = useState(null);

  const countOf = (status) => requests.filter((request) => request.status === status).length;
  const filtered = filter === 'all' ? requests : requests.filter((request) => request.status === filter);
  const formatAmount = (amount) => `₺${(amount || 0).toLocaleString(locale)}`;

  const stats = [
    { key: 'all', value: requests.length, tone: 'tw-text-slate-900 dark:tw-text-white' },
    { key: 'pending', value: countOf('pending'), tone: 'tw-text-amber-600 dark:tw-text-amber-300' },
    { key: 'approved', value: countOf('approved'), tone: 'tw-text-emerald-600 dark:tw-text-emerald-300' },
    { key: 'rejected', value: countOf('rejected'), tone: 'tw-text-rose-600 dark:tw-text-rose-300' },
  ];

  let body;
  if (loading) {
    body = <LoadingRows count={3} height="tw-h-28" />;
  } else if (requests.length === 0) {
    body = (
      <EmptyState
        icon={LuReceipt}
        title={label('empty')}
        text={label('emptyHint')}
        action={<Button variant="primary" size="sm" onClick={onNewRequest}><LuPlus aria-hidden="true" className="tw-h-4 tw-w-4" />{label('newRequest')}</Button>}
      />
    );
  } else if (filtered.length === 0) {
    body = <EmptyState compact icon={LuReceipt} title={label('noMatch')} />;
  } else {
    body = (
      <ul className="tw-grid tw-gap-3 md:tw-grid-cols-2">
        {filtered.map((request) => {
          const StatusIcon = STATUS_ICONS[request.status] || LuClock;
          const pdfs = attachedPdfs(request);
          return (
            <li key={request.id} className={`${CARD} tw-flex tw-flex-col tw-gap-3 tw-p-4`}>
              <div className="tw-flex tw-items-start tw-justify-between tw-gap-2">
                <span className="tw-text-2xl tw-font-semibold tw-tracking-tight tw-tabular-nums tw-text-slate-900 dark:tw-text-white">{formatAmount(request.amount)}</span>
                {EXPENSE_STATUS_TONES[request.status] && (
                  <Badge tone={EXPENSE_STATUS_TONES[request.status]}>
                    <StatusIcon aria-hidden="true" className="tw-h-3 tw-w-3" />
                    {t(`profilePage.expense.status.${request.status}`)}
                  </Badge>
                )}
              </div>
              <p className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-3 tw-gap-y-1 tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">
                <span className="tw-inline-flex tw-items-center tw-gap-1"><LuTag aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />{expenseCategoryLabel(t, request.category)}</span>
                <span className="tw-inline-flex tw-items-center tw-gap-1 tw-tabular-nums">
                  <LuCalendarDays aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
                  {request.createdAt ? new Date(request.createdAt).toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' }) : '-'}
                </span>
              </p>
              {request.description && <p className="tw-whitespace-pre-line tw-break-words tw-text-sm tw-text-slate-700 dark:tw-text-slate-200">{request.description}</p>}

              {pdfs.length > 0 && (
                <ul className="tw-flex tw-flex-wrap tw-gap-1.5">
                  {pdfs.map((pdf, index) => (
                    <li key={pdf.url} className="tw-block">
                      <a
                        href={pdf.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="tw-inline-flex tw-max-w-[14rem] tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-slate-500/10 tw-px-2.5 tw-py-1 tw-text-xs tw-font-medium tw-text-slate-700 hover:tw-bg-slate-500/15 dark:tw-text-slate-200"
                      >
                        <LuFileText aria-hidden="true" className="tw-h-3.5 tw-w-3.5 tw-shrink-0 tw-text-rose-500" />
                        <span className="tw-truncate">{pdf.name || label('documentN', { n: index + 1 })}</span>
                      </a>
                    </li>
                  ))}
                </ul>
              )}

              {request.status === 'rejected' && request.rejectionReason && (
                <div className="tw-rounded-xl tw-bg-rose-500/10 tw-px-3.5 tw-py-2.5 tw-text-sm tw-text-rose-900 dark:tw-text-rose-100">
                  <p className="tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-opacity-70">{label('rejectionReason')}</p>
                  <p className="tw-mt-0.5 tw-whitespace-pre-line tw-break-words">{request.rejectionReason}</p>
                </div>
              )}

              <div className="tw-mt-auto tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
                {request.status !== 'pending' && request.updatedAt ? (
                  <span className="tw-text-xs tw-text-slate-400 dark:tw-text-slate-500">
                    {label('processedOn', { date: new Date(request.updatedAt).toLocaleString(locale, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) })}
                  </span>
                ) : <span className="tw-text-xs tw-text-slate-400 dark:tw-text-slate-500">{request.status === 'pending' ? label('pendingHint') : ''}</span>}
                {request.status === 'pending' && (
                  <Button size="sm" onClick={() => setEditingExpense(request)}>
                    <LuPencil aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
                    {label('edit')}
                  </Button>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <div className="tw-space-y-4">
      <SectionHeader
        icon={LuReceipt}
        gradient="tw-from-emerald-400 tw-to-teal-600 tw-shadow-emerald-500/30"
        title={label('title')}
        description={label('description')}
      >
        <Button size="sm" onClick={onNewRequest}>
          <LuPlus aria-hidden="true" className="tw-h-4 tw-w-4" />
          {label('newRequest')}
        </Button>
      </SectionHeader>

      {!loading && requests.length > 0 && (
        <>
          <dl className="tw-grid tw-grid-cols-2 tw-gap-2 sm:tw-grid-cols-4">
            {stats.map((stat) => (
              <div key={stat.key} className="tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-white/60 tw-px-3 tw-py-2.5 dark:tw-border-white/10 dark:tw-bg-white/[0.03]">
                <dd className={`tw-text-xl tw-font-semibold tw-leading-none tw-tabular-nums ${stat.tone}`}>{stat.value}</dd>
                <dt className="tw-mt-1 tw-text-[11px] tw-font-medium tw-text-slate-500 dark:tw-text-slate-400">{stat.key === 'all' ? label('total') : t(`profilePage.expense.status.${stat.key}`)}</dt>
              </div>
            ))}
          </dl>
          <div className="tw-overflow-x-auto tw-pb-1">
            <SegmentedControl
              size="sm"
              layoutId="profile-expense-filter"
              label={label('filterLabel')}
              value={filter}
              onChange={setFilter}
              options={['all', ...STATUSES].map((key) => ({ value: key, label: key === 'all' ? label('all') : t(`profilePage.expense.status.${key}`) }))}
            />
          </div>
        </>
      )}

      {body}

      <EditExpenseDialog
        expense={editingExpense}
        employeeId={employeeId}
        onClose={() => setEditingExpense(null)}
        onSaved={(id, updateData) => setRequests((prev) => prev.map((request) => (request.id === id ? { ...request, ...updateData } : request)))}
      />
    </div>
  );
};

ExpenseTracking.propTypes = {
  requests: PropTypes.arrayOf(PropTypes.object).isRequired,
  setRequests: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  employeeId: PropTypes.string,
  onNewRequest: PropTypes.func.isRequired,
};

export default ExpenseTracking;
